import { InjectionToken } from '@angular/core';

/**
 * Host-based default brand for the Linx customer app.
 *
 * Capture-send is normally themed per-workflow at runtime by {@link ThemeService}
 * (from the API's `LinxBranding`). This adds the *default* brand identity used when a
 * workflow carries no custom branding: on a "salt" host the default is SALT, everywhere
 * else it stays Cateno. Per-workflow custom branding always wins over either default.
 */
export enum Brand {
  Salt = 'salt',
  Cateno = 'cateno',
}

export interface BrandConfig {
  brand: Brand;
  /** Human-readable brand name (page title). */
  displayName: string;
  /** Default header/card logo shown when a workflow has no custom logoUrl. */
  logo: string;
  /**
   * Invert the default logo when it's rendered on the (light) landing card.
   * SALT's official wordmark asset is white-on-transparent (built for dark
   * backgrounds), so it's flipped to dark here; Cateno's mark is already dark.
   */
  defaultLogoInvert: boolean;
  /** Alt text for the default logo. */
  logoAlt: string;
  /** Text wordmark rendered next to the default logo (empty = none). */
  wordmark: string;
  /** Path to the favicon asset. */
  favicon: string;
  /** Default primary color when a workflow carries no custom branding. */
  defaultPrimary: string;
  /** Legal operating entity shown in customer-facing footers/copyright. */
  companyName: string;
  /** Postal address lines for the operating entity (capture-send footer). */
  companyAddressLines: string[];
  /** External support/contact page. */
  contactUrl: string;
  /** External Terms of Service / legal page. */
  legalUrl: string;
  /** External Privacy Policy page. */
  privacyUrl: string;
}

export const BRAND_CONFIGS: Record<Brand, BrandConfig> = {
  [Brand.Cateno]: {
    brand: Brand.Cateno,
    displayName: 'RocketCapture',
    logo: 'assets/images/cateno-logo.svg',
    defaultLogoInvert: false,
    logoAlt: 'cateno.ai',
    wordmark: '', // the logo is already a "cateno.ai" wordmark, so no extra text
    favicon: './assets/images/favicon.svg',
    defaultPrimary: '#FF7403',
    companyName: 'Rocket Financial Inc.',
    companyAddressLines: ['21550 Biscayne Blvd, Suite 400', 'Aventura, Florida 33180, USA'],
    contactUrl: 'https://rocketfncl.com/contact/',
    legalUrl: 'https://rocketfncl.com/legal/',
    privacyUrl: 'https://rocketfncl.com/privacy-policy/',
  },
  [Brand.Salt]: {
    brand: Brand.Salt,
    displayName: 'Salt',
    // Official Salt wordmark (same asset as portal-client). It's white-on-transparent,
    // so `defaultLogoInvert` flips it to dark for the white landing card.
    logo: 'assets/images/salt-logo-white.png',
    defaultLogoInvert: true,
    logoAlt: 'salt',
    wordmark: '', // the logo is already a "salt" wordmark, so no extra text
    favicon: './assets/images/salt-favicon.ico',
    defaultPrimary: '#2fa8bc',
    companyName: 'Salt Treasury, Inc.',
    // TODO(SALT): confirm the registered operating-entity address.
    companyAddressLines: ['[SALT address line 1]', '[SALT address line 2]'],
    // TODO(SALT): confirm the exact contact path on salttreasury.com.
    contactUrl: 'https://salttreasury.com/contact',
    // Legal pages confirmed by the Salt Treasury website launch (KOR-14864).
    legalUrl: 'https://salttreasury.com/terms',
    privacyUrl: 'https://salttreasury.com/privacy',
  },
};

// TEMP: force a brand during local development so SALT branding can be previewed
// without a "salt" host. Set back to `null` to restore pure host-based detection
// locally. Deployed hosts are unaffected.
const LOCAL_DEV_BRAND: Brand | null = null;

const isLocalHost = (host: string): boolean => host.startsWith('localhost') || host.startsWith('127.0.0.1');

/**
 * Resolves the default brand from the host. A host containing the word "salt" activates
 * SALT; everything else falls back to Cateno (the existing default).
 */
export const getBrand = (host: string = window.location.host): Brand => {
  if (LOCAL_DEV_BRAND && isLocalHost(host)) {
    return LOCAL_DEV_BRAND;
  }

  return host.toLowerCase().includes('salt') ? Brand.Salt : Brand.Cateno;
};

export const getBrandConfig = (host?: string): BrandConfig => BRAND_CONFIGS[getBrand(host)];

// Root-provided with a default factory so the token is always resolvable — in the
// app and in any TestBed — without each injector needing to register it explicitly.
export const BRAND_CONFIG = new InjectionToken<BrandConfig>('Brand Config', {
  providedIn: 'root',
  factory: () => getBrandConfig(),
});
